/**
 * The queues (wave W1, coherence review R13 and ruling 4).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * A queue is a register opened PRE-FILTERED to what is waiting on the person. The walk
 * found Home tiles that counted one set and landed on a page showing another (a tile
 * counted 5 results at final approval; the page listed the whole inspections register).
 * The cure is structural: each queue below has ONE read, and both the count on Home and
 * the list on the queue page come from that read. A queue cannot count one number and
 * show another, because there is only one number.
 *
 * Where the server answers "waiting on THIS person" (pending_for_me, wave W1 backend)
 * the queue counts ACTIONABLE records. Where no such answer exists yet (findings), the
 * queue counts the records the person can see in the waiting states, the page lists the
 * same states, and the label says so plainly.
 */

import { listFindings, type FindingSummary } from "../api/findings";
import { listItps, type ItpSummary } from "../api/itps";
import { listSchedules, type ScheduleSummary } from "../api/schedules";
import { listVerifications, type VerificationSummary } from "../api/verifications";
import { strings } from "../brand/strings";
import type { PersonaShape } from "./personaNav";

/** The finding states the vocabulary counts as open, in the engine's own tokens. */
export const OPEN_FINDING_STATES = [
  "OPEN",
  "RECORDED",
  "IN_PUNCH_LIST",
  "AWAITING_RESPONSE",
  "RESPONSE_PROPOSED",
  "AGREED",
  "IMPLEMENTED",
  "PUNCH_COMPLETED",
] as const;

/** The verification states that are over: an inspection in one is no longer open work. */
export const DONE_VERIFICATION_STATES: ReadonlySet<string> = new Set(["CLOSED", "CANCELLED"]);

/** The `?queue=` values the registers read. One per register queue. */
export const QUEUE_PARAM = {
  itpReview: "review",
  scheduleSign: "sign",
  resultApproval: "approve",
  myInspections: "mine",
  /** Every inspection assigned to the caller, open or over (the dashboard's tile). */
  assignedInspections: "assigned",
} as const;

// ---------------------------------------------------------------------------------
// The register-side reads. A register in queue mode calls exactly these, and the Home
// counts call exactly these: the one-read rule above.
// ---------------------------------------------------------------------------------

/** Plans in review the caller may approve or return (server: pending_for_me). */
export async function readItpReviewQueue(
  token: string,
  signal: AbortSignal,
): Promise<ItpSummary[]> {
  const body = await listItps(token, { pendingForMe: true, signal });
  return body.items;
}

/** Schedules waiting on the caller's endorsement or approval (server: pending_for_me). */
export async function readScheduleSignQueue(
  token: string,
  signal: AbortSignal,
): Promise<ScheduleSummary[]> {
  const body = await listSchedules(token, { pendingForMe: true, signal });
  return body.items;
}

/** Results at final approval the caller may approve (server: pending_for_me). */
export async function readResultApprovalQueue(
  token: string,
  signal: AbortSignal,
): Promise<VerificationSummary[]> {
  const body = await listVerifications(token, { pendingForMe: true, signal });
  return body.items;
}

/** The inspections assigned to the caller that are not over (server: mine). */
export async function readMyInspectionsQueue(
  token: string,
  signal: AbortSignal,
): Promise<VerificationSummary[]> {
  const body = await listVerifications(token, { mine: true, signal });
  return body.items.filter((row) => !DONE_VERIFICATION_STATES.has(row.workflowState));
}

/** Every inspection assigned to the caller, in any state (server: mine). */
export async function readAssignedInspections(
  token: string,
  signal: AbortSignal,
): Promise<VerificationSummary[]> {
  return (await listVerifications(token, { mine: true, signal })).items;
}

/**
 * The register route a findings filter opens: the state set in the URL, comma-joined.
 * A QUEUE adds its own name, so the register can say it is a queue (the banner).
 */
export function findingsRoute(states: readonly string[], queue?: QueueId): string {
  const base = `/findings?state=${states.join(",")}`;
  return queue === undefined ? base : `${base}&queue=${queue}`;
}

// ---------------------------------------------------------------------------------
// The queues as Home shows them.
// ---------------------------------------------------------------------------------

export type QueueId =
  | "itpReview"
  | "scheduleSign"
  | "resultApproval"
  | "rectifications"
  | "responsesToDecide"
  | "myInspections"
  | "openFindings"
  | "responseStep";

const QUEUE_IDS: readonly QueueId[] = [
  "itpReview",
  "scheduleSign",
  "resultApproval",
  "rectifications",
  "responsesToDecide",
  "myInspections",
  "openFindings",
  "responseStep",
];

/** Whether a URL value names a queue. */
export function isQueueId(value: string | null): value is QueueId {
  return value !== null && (QUEUE_IDS as readonly string[]).includes(value);
}

/** One waiting record as a queue card lists it. */
export interface QueueItem {
  readonly id: string;
  readonly humanId: string;
  readonly title: string;
  readonly state: string;
  readonly to: string;
}

/** What a queue read answers: the whole count and the first records. */
export interface QueueAnswer {
  readonly total: number;
  readonly items: readonly QueueItem[];
}

export interface QueueDef {
  readonly id: QueueId;
  readonly label: string;
  readonly empty: string;
  /** Where the queue opens: the register, pre-filtered to exactly this set. */
  readonly route: string;
  readonly read: (token: string, signal: AbortSignal) => Promise<QueueAnswer>;
  /**
   * The wider set in the same waiting state that is NOT the person's to act on, when it
   * helps to know it exists (results at final approval waiting on somebody else).
   */
  readonly watch?: {
    readonly route: string;
    readonly read: (token: string, signal: AbortSignal) => Promise<number>;
    readonly text: (count: number) => string;
    readonly link: string;
  };
}

const itpItem = (row: ItpSummary): QueueItem => ({
  id: row.id,
  humanId: row.humanId,
  title: row.title,
  state: row.workflowState,
  to: `/itps/${row.id}`,
});

const scheduleItem = (row: ScheduleSummary): QueueItem => ({
  id: row.id,
  humanId: row.humanId,
  title: row.title,
  state: row.workflowState,
  to: `/schedules/${row.id}`,
});

const verificationItem = (row: VerificationSummary): QueueItem => ({
  id: row.id,
  humanId: row.humanId,
  title: row.title,
  state: row.workflowState,
  to: `/inspections/${row.id}`,
});

const findingItem = (row: FindingSummary): QueueItem => ({
  id: row.id,
  humanId: row.humanId,
  title: row.description,
  state: row.findingStatus,
  to: `/findings/${row.id}`,
});

/** How many records a queue card lists before it says "and N more". */
export const QUEUE_PREVIEW = 5;

function answer<T>(rows: readonly T[], toItem: (row: T) => QueueItem): QueueAnswer {
  return { total: rows.length, items: rows.slice(0, QUEUE_PREVIEW).map(toItem) };
}

function findingsQueue(
  id: QueueId,
  copy: { readonly label: string; readonly empty: string },
  states: readonly string[],
): QueueDef {
  return {
    id,
    label: copy.label,
    empty: copy.empty,
    route: findingsRoute(states, id),
    read: async (token, signal) => {
      const body = await listFindings(token, {
        findingStatus: states,
        limit: QUEUE_PREVIEW,
        signal,
      });
      return { total: body.total, items: body.items.map(findingItem) };
    },
  };
}

export const QUEUES: Readonly<Record<QueueId, QueueDef>> = {
  itpReview: {
    id: "itpReview",
    label: strings.queues.itpReview.label,
    empty: strings.queues.itpReview.empty,
    route: `/itps?queue=${QUEUE_PARAM.itpReview}`,
    read: async (token, signal) => answer(await readItpReviewQueue(token, signal), itpItem),
  },
  scheduleSign: {
    id: "scheduleSign",
    label: strings.queues.scheduleSign.label,
    empty: strings.queues.scheduleSign.empty,
    route: `/schedules?queue=${QUEUE_PARAM.scheduleSign}`,
    read: async (token, signal) => answer(await readScheduleSignQueue(token, signal), scheduleItem),
  },
  resultApproval: {
    id: "resultApproval",
    label: strings.queues.resultApproval.label,
    empty: strings.queues.resultApproval.empty,
    route: `/inspections?queue=${QUEUE_PARAM.resultApproval}`,
    read: async (token, signal) =>
      answer(await readResultApprovalQueue(token, signal), verificationItem),
    watch: {
      route: "/inspections?state=FINAL_APPROVAL",
      read: async (token, signal) =>
        (await listVerifications(token, { workflowState: "FINAL_APPROVAL", signal })).total,
      text: strings.queues.resultApproval.watch,
      link: strings.queues.resultApproval.watchLink,
    },
  },
  myInspections: {
    id: "myInspections",
    label: strings.queues.myInspections.label,
    empty: strings.queues.myInspections.empty,
    route: `/inspections?queue=${QUEUE_PARAM.myInspections}`,
    read: async (token, signal) =>
      answer(await readMyInspectionsQueue(token, signal), verificationItem),
  },
  rectifications: findingsQueue("rectifications", strings.queues.rectifications, ["IMPLEMENTED"]),
  responsesToDecide: findingsQueue("responsesToDecide", strings.queues.responsesToDecide, [
    "RESPONSE_PROPOSED",
  ]),
  openFindings: findingsQueue("openFindings", strings.queues.openFindings, OPEN_FINDING_STATES),
  responseStep: findingsQueue("responseStep", strings.queues.responseStep, [
    "AWAITING_RESPONSE",
    "RESPONSE_PROPOSED",
  ]),
};

/**
 * The queues each persona works, from the flows and the shipped policy:
 * - the QC lead reviews plans (ITP APPROVE/RETURN, tenant), endorses schedules
 *   (SCHEDULE ENDORSE), and watches results and rectifications. She holds no
 *   APPROVE_RESULTS, so her results queue is truthfully empty and its watch line
 *   says how many wait on somebody else;
 * - the head of projects approves schedules (SCHEDULE APPROVE);
 * - the QA/QC engineer reviews plans and approves results (ITP APPROVE and
 *   VERIFICATION APPROVE_RESULTS at contract scope) and decides responses;
 * - the inspector conducts his assigned inspections and verifies rectifications;
 * - the contractor answers findings; the TPI attends its assigned inspections;
 * - management reads: no queue of its own (ruling 5, no data entry).
 */
export const PERSONA_QUEUES: Readonly<Record<PersonaShape, readonly QueueId[]>> = {
  qcLead: ["itpReview", "scheduleSign", "resultApproval", "rectifications"],
  manager: ["scheduleSign"],
  management: [],
  planner: ["itpReview", "resultApproval", "responsesToDecide", "rectifications"],
  inspector: ["myInspections", "responsesToDecide", "rectifications"],
  contractor: ["openFindings", "responseStep"],
  tpi: ["myInspections"],
};
